import axios from "axios";
import ensureErrorResponse from "@/utils/ensureErrorResponse";
import router from "@/router";
import { legacyParamsSerializer } from "./legacyParamsSerializer";

// axios 1.x는 중첩 객체 params를 bracket notation으로 flatten하지만,
// 백엔드 DTO는 v0 시절의 JSON.stringify 결과(단일 문자열)를 전제로 설계됨.
// 전역 기본 + 인스턴스 모두에 v0 호환 직렬화를 주입해 raw axios.* 사용처까지 커버.
axios.defaults.paramsSerializer = { serialize: legacyParamsSerializer };

// 추후 공용 사용때 해제
// const isLocal = process.env.NODE_ENV === 'development';
// const instance = axios.create({
//   baseURL: isLocal ? '' : process.env.VUE_APP_HOST,
//   withCredentials: true,
// });

// MRI 프리뷰(VUE_APP_MRI_BRIDGE=1 빌드)에서만: MRI가 브릿지로 넣어 둔 브랜치 API 주소를 쓴다. 운영 빌드는 이 값이 없어 그대로.
function resolveApiHost() {
  if (process.env.VUE_APP_MRI_BRIDGE === "1") {
    try {
      const override = window.localStorage.getItem("mri_api_host");
      if (override && /^https:\/\//.test(override)) return override.replace(/\/$/, "");
    } catch (e) {
      // 저장소 접근 불가 — 기본값
    }
  }
  return process.env.VUE_APP_HOST;
}

const instance = axios.create({
  baseURL: resolveApiHost(),
  withCredentials: true,
  paramsSerializer: { serialize: legacyParamsSerializer },
});

instance.interceptors.request.use(
  config => {
    const accessToken = localStorage.getItem("naezip_to_ac_se");
    try {
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      return config;
    } catch (err) {
      console.error("[_axios.interceptors.request] config : " + err);
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(accessToken) {
  refreshSubscribers.forEach(callback => callback(accessToken));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
  refreshSubscribers.push(callback);
}

const REFRESH_TOKEN_ENDPOINT = "/api/v1/user/auth/refresh";
const EXCHANGE_TOKEN_ENDPOINT = "/api/v1/user/auth/exchange";

instance.interceptors.response.use(
  response => {
    return response;
  },

  async error => {
    ensureErrorResponse(error);
    const { config, response } = error;
    const originalRequest = config;
    // return Promise.reject(error);

    if (response && response.status === 401) {
      // 프리뷰 토큰(NZSC-P-ENC) 요청의 401 은 고객 세션과 무관하다 —
      // refresh 시도·localStorage.clear()·리로드 어느 것도 하면 안 된다
      // (만료된 관리자 미리보기 링크를 로그인 상태로 열면 고객이 로그아웃되던 결함).
      if (originalRequest?.skipAuthRefresh) {
        return Promise.reject(error);
      }
      if (originalRequest.url === EXCHANGE_TOKEN_ENDPOINT) {
        return Promise.reject(error);
      } else if (originalRequest.url === REFRESH_TOKEN_ENDPOINT) {
        isRefreshing = false;
        onRefreshed(null);

        // 리프레시 토큰 획득 실패 시 전체 로그아웃 처리.
        if (window.localStorage.getItem("naezip_to_ac_se")) {
          window.console.log("refresh endpoint 401");

          // 무한 리로드 방어: 앱 웹뷰에서 손상/만료 access 토큰이 (앱 쿠키·서버측 리프레시 등으로)
          // 매 로드 재공급되면 "clear→logout→router.go(reload)"가 무한 반복돼 전체 백지가 된다
          // (실사용자 재현 완료). 세션 기준 복구 리로드를 1회로 제한하고, 그 이후엔 리로드하지 않고
          // 익명 상태로 앱을 렌더한다(백지 대신 홈 노출). sessionStorage는 localStorage.clear()에
          // 영향받지 않고 리로드를 넘어 유지되므로 카운터 저장소로 안전하다.
          const AUTH_RECOVER_KEY = "_auth_recover_reloads";
          let recoverReloads = 0;
          try {
            recoverReloads =
              parseInt(window.sessionStorage.getItem(AUTH_RECOVER_KEY) || "0", 10) || 0;
          } catch (e) {
            /* sessionStorage 접근 실패 무시 */
          }

          // 다방 인입 세션(NZSC-3205): refresh 미발급(24h 단명)이라 만료 시 여기로 온다.
          // 비번/소셜 로그인이 없는 계정이므로 "다방에서 재진입" 안내 후 로그아웃 — 무설명 홈 튕김 방지.
          let isDabangSession = false;
          try {
            isDabangSession = window.sessionStorage.getItem("dabang_entered") === "1";
          } catch (e) {
            /* sessionStorage 접근 실패 무시 */
          }
          if (isDabangSession && recoverReloads < 1) {
            window.alert(
              "이용 시간이 만료되었어요.\n다방 앱에서 내집스캔을 다시 열어주시면 이어서 보실 수 있어요.",
            );
          }

          window.localStorage.clear();
          // window.localStorage.removeItem("naezip_to_ac_se");
          // window.localStorage.removeItem("vuex");

          // 리프레시 토큰 클리어.
          try {
            await instance.post("/api/v1/user/logout");
          } catch (e) {
            /* 로그아웃 실패해도 복구 흐름은 계속 */
          }

          if (recoverReloads < 1) {
            // 정상 만료 복구: 토큰 정리 후 1회 새로고침 → 익명으로 재진입.
            try {
              window.sessionStorage.setItem(AUTH_RECOVER_KEY, String(recoverReloads + 1));
            } catch (e) {
              /* sessionStorage 접근 실패 무시 */
            }
            router.go();
          } else {
            // 이미 1회 복구 리로드를 했는데 또 왔다 = 손상 상태가 재공급되는 중.
            // 더 리로드하면 무한 백지 → 리로드하지 않고 익명 렌더 유지.
            window.console.warn("auth recovery loop detected — staying anonymous without reload");
          }
        }
        return Promise.reject(error);
      } else if (!originalRequest._retry) {
        window.console.log("isRefreshing", isRefreshing, originalRequest.url);
        if (isRefreshing) {
          // 리프레시 진행 중인 경우 큐에 요청 추가하고 리프레시 완료 후 재시도.
          return new Promise((resolve, reject) => {
            addRefreshSubscriber(accessToken => {
              if (accessToken) {
                originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
                resolve(instance(originalRequest));
              } else {
                reject(error);
              }
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // 리프레시 엔드포인트 호출.
          const refreshResponse = await instance.get(REFRESH_TOKEN_ENDPOINT);
          const newToken = refreshResponse?.data?.data?.session;
          if (newToken) {
            // 새 엑세스 토큰 획득 시 로컬스토리지에 저장.
            window.localStorage.setItem("naezip_to_ac_se", newToken);

            // 액세스 토큰 획득했으므로 큐에 있는 요청들 재시도.
            onRefreshed(newToken);

            // 액세스 토큰 업데이트 후 기존 요청 재시도.
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return instance(originalRequest);
          }
        } catch (refreshError) {
          return Promise.reject(ensureErrorResponse(refreshError));
        } finally {
          isRefreshing = false;
        }
      }
    }

    return Promise.reject(error);
  },
);

export default instance;
